import React, { useEffect, useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
  useMediaQuery
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { FormContainer, SectionCard, SectionTitle, Row, Col3, Col4, Col6, Col8, Campo } from './styles'
import { ButtonSalvar } from '../ButtonsCrud/style'
import NotaFiscalProdutos from '../NotaFiscalProdutos'
import ProdutoNotaDevolucao from '../../../interfaces/produtoNotaDevolucao'
import { isStatusSucessoNotaFiscal } from '../../../utils/Constants'

export interface NotaFiscalNovaEntrada {
  tipo: number
  data: string
  valor: number
  transporte: {
    tipoFrete: string
    nome: string
    cnpj: string
    inscricaoEstadual: string
    endereco: string
    cidade: string
    uf: string
  }
  volumens: {
    quantidadeVolumes: number
    pesoLiquido: number
    pesoBruto: number
  }
}

interface EnderecoDestinatario {
  logradouro?: string
  numero?: string
  complemento?: string
  bairro?: string
  codigoMunicipio?: number | string
  municipio?: string
  uf?: string
  cep?: string
  telefone?: string
  codigoPais?: number | string
  pais?: string | null
}

export interface NotaFiscalDetalhada extends NotaFiscalNovaEntrada {
  id: string
  numeroNFeOrigem: string
  nFeReferencia: string
  destinatario: {
    nome: string
    documento?: string
    cnpj?: string
    inscricaoEstadual?: string
    ie?: string
    cep: string
    endereco: string | EnderecoDestinatario
    numero: string
    complemento: string
    bairro: string
    codigoMunicipio: string
    cidade: string
    uf: string
    telefone?: string
  }
  itens?: ProdutoNotaDevolucao[]
  statusRetorno?: number
  mensagemRetorno?: string
}

export type NotaFiscalPreenchimento = Omit<NotaFiscalDetalhada, 'id' | 'statusRetorno' | 'mensagemRetorno'>

const tipoDescricaoNotaFiscal: Record<number, string> = {
  1: 'NFCe',
  2: 'Estorno',
  3: 'Contingência',
  4: 'Devolução',
  5: 'NFe'
}

interface NotaFiscalDialogProps {
  aberto: boolean
  setAberto: React.Dispatch<React.SetStateAction<boolean>>
  onAdicionar: (notaFiscal: NotaFiscalPreenchimento) => void
  entidade?: NotaFiscalDetalhada | null
  onAtualizar?: (notaFiscal: NotaFiscalDetalhada) => void
  somenteLeitura?: boolean
  valoresIniciais?: NotaFiscalPreenchimento | null
  onGerarEspelho?: (notaFiscal: NotaFiscalPreenchimento) => void
}

const NotaFiscalDialog: React.FC<NotaFiscalDialogProps> = ({
  aberto,
  setAberto,
  onAdicionar,
  entidade,
  onAtualizar,
  somenteLeitura = false,
  valoresIniciais,
  onGerarEspelho
}) => {
  const theme = useTheme()
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
  const tipoDevolucao = 4 // ETipoNotaFiscal.NFeDevolucao

  const [tipo] = useState<number>(tipoDevolucao)
  const [numeroNFeOrigem, setNumeroNFeOrigem] = useState<string>('')
  const [destinatario, setDestinatario] = useState<string>('')
  const [documento, setDocumento] = useState<string>('')
  const [inscricaoEstadual, setInscricaoEstadual] = useState<string>('')
  const [cep, setCep] = useState<string>('')
  const [endereco, setEndereco] = useState<string>('')
  const [numeroEndereco, setNumeroEndereco] = useState<string>('')
  const [complemento, setComplemento] = useState<string>('')
  const [bairro, setBairro] = useState<string>('')
  const [codigoMunicipio, setCodigoMunicipio] = useState<string>('')
  const [cidade, setCidade] = useState<string>('')
  const [uf, setUf] = useState<string>('')
  const [telefone, setTelefone] = useState<string>('')
  const [transportadora, setTransportadora] = useState<string>('')
  const [tipoFrete, setTipoFrete] = useState<string>('Emitente')
  const [transportadoraCnpj, setTransportadoraCnpj] = useState<string>('')
  const [transportadoraIe, setTransportadoraIe] = useState<string>('')
  const [transportadoraEndereco, setTransportadoraEndereco] = useState<string>('')
  const [transportadoraCidade, setTransportadoraCidade] = useState<string>('')
  const [transportadoraUf, setTransportadoraUf] = useState<string>('')
  const [quantidadeVolumes, setQuantidadeVolumes] = useState<string>('')
  const [pesoLiquido, setPesoLiquido] = useState<string>('')
  const [pesoBruto, setPesoBruto] = useState<string>('')
  const [produtosCarregados, setProdutosCarregados] = useState<ProdutoNotaDevolucao[]>([])
  const [produtos, setProdutos] = useState<ProdutoNotaDevolucao[]>([])

  const semFreteSelecionado = tipoFrete === 'Sem Frete'

  const onlyDigits = (value: string) => value.replace(/\D/g, '')

  const limparCamposTransporte = () => {
    setTransportadora('')
    setTransportadoraCnpj('')
    setTransportadoraIe('')
    setTransportadoraEndereco('')
    setTransportadoraCidade('')
    setTransportadoraUf('')
    setQuantidadeVolumes('')
    setPesoLiquido('')
    setPesoBruto('')
  }

  const limparFormulario = () => {
    setNumeroNFeOrigem('')
    setDestinatario('')
    setDocumento('')
    setInscricaoEstadual('')
    setCep('')
    setEndereco('')
    setNumeroEndereco('')
    setComplemento('')
    setBairro('')
    setCodigoMunicipio('')
    setCidade('')
    setUf('')
    setTelefone('')
    setTipoFrete('Emitente')
    limparCamposTransporte()
    setProdutosCarregados([])
    setProdutos([])
  }

  const handleFechar = () => {
    limparFormulario()
    setAberto(false)
  }

  useEffect(() => {
    if (semFreteSelecionado) {
      limparCamposTransporte()
    }
  }, [semFreteSelecionado])

  useEffect(() => {
    if (!aberto) {
      return
    }

    const fonte: NotaFiscalPreenchimento | NotaFiscalDetalhada | null | undefined = entidade || valoresIniciais

    if (!fonte) {
      return
    }

    const enderecoDestinatario =
      fonte.destinatario?.endereco && typeof fonte.destinatario.endereco === 'object'
        ? fonte.destinatario.endereco
        : undefined

    setNumeroNFeOrigem(fonte.numeroNFeOrigem || fonte.nFeReferencia || '')
    setDestinatario(fonte.destinatario?.nome || '')
    setDocumento(fonte.destinatario?.documento || fonte.destinatario?.cnpj || '')
    setInscricaoEstadual(fonte.destinatario?.inscricaoEstadual || fonte.destinatario?.ie || '')
    setCep(enderecoDestinatario?.cep || fonte.destinatario?.cep || '')
    setEndereco(enderecoDestinatario?.logradouro || '')
    setNumeroEndereco(enderecoDestinatario?.numero || fonte.destinatario?.numero || '')
    setComplemento(enderecoDestinatario?.complemento || fonte.destinatario?.complemento || '')
    setBairro(enderecoDestinatario?.bairro || fonte.destinatario?.bairro || '')
    setCodigoMunicipio(
      enderecoDestinatario?.codigoMunicipio != null
        ? String(enderecoDestinatario.codigoMunicipio)
        : (fonte.destinatario?.codigoMunicipio || '')
    )
    setCidade(enderecoDestinatario?.municipio || fonte.destinatario?.cidade || '')
    setUf(enderecoDestinatario?.uf || fonte.destinatario?.uf || '')
    setTelefone(enderecoDestinatario?.telefone || fonte.destinatario?.telefone || '')
    setTipoFrete(fonte.transporte?.tipoFrete || 'Emitente')
    setTransportadora(fonte.transporte?.nome || '')
    setTransportadoraCnpj(fonte.transporte?.cnpj || '')
    setTransportadoraIe(fonte.transporte?.inscricaoEstadual || '')
    setTransportadoraEndereco(fonte.transporte?.endereco || '')
    setTransportadoraCidade(fonte.transporte?.cidade || '')
    setTransportadoraUf(fonte.transporte?.uf || '')
    setQuantidadeVolumes(
      fonte.volumens?.quantidadeVolumes != null ? String(fonte.volumens.quantidadeVolumes) : ''
    )
    setPesoLiquido(
      fonte.volumens?.pesoLiquido != null ? String(fonte.volumens.pesoLiquido) : ''
    )
    setPesoBruto(
      fonte.volumens?.pesoBruto != null ? String(fonte.volumens.pesoBruto) : ''
    )
    const itensCarregados = Array.isArray(fonte.itens) ? fonte.itens : []

    setProdutosCarregados(itensCarregados)
    setProdutos(itensCarregados)
  }, [aberto, entidade, valoresIniciais])

  const produtosNaoSelecionados = useMemo(() => {
    const numerosItensSelecionados = new Set(produtos.map((produto) => produto.numeroItem))
    return produtosCarregados.filter((produto) => !numerosItensSelecionados.has(produto.numeroItem))
  }, [produtosCarregados, produtos])

  const valorTotal = useMemo(() => {
    return produtos.reduce((acumulado, produto) => acumulado + (produto.quantidade * (produto.valor || 0)), 0)
  }, [produtos])

  const camposSomenteLeituraProps = somenteLeitura
    ? { InputProps: { readOnly: true }, disabled: true }
    : {}
  const statusRetornoVisivel = entidade != null
  const statusRetornoPendente = entidade?.statusRetorno == null || entidade.statusRetorno === 0
  const statusRetornoAutorizado = entidade?.statusRetorno != null
    && isStatusSucessoNotaFiscal(entidade.statusRetorno)
  const statusRetornoTexto = statusRetornoPendente
    ? 'Pendente'
    : statusRetornoAutorizado
      ? 'Autorizado'
      : 'Rejeitado'
  const statusRetornoCor = statusRetornoPendente
    ? '#9e9e9e'
    : statusRetornoAutorizado
      ? 'green'
      : 'red'

  const tituloDialog = somenteLeitura
    ? 'Visualizar Nota Fiscal'
    : entidade
      ? 'Editar Nota Fiscal'
      : 'Nova Nota Fiscal'

  const tipoDescricaoAtual = entidade?.tipo != null
    ? (tipoDescricaoNotaFiscal[entidade.tipo] ?? 'Devolução')
    : 'Devolução'

  const construirPayload = (): NotaFiscalPreenchimento => {
    const quantidadeVolumesNumero = Number(quantidadeVolumes)
    const pesoLiquidoNumero = parseFloat(pesoLiquido.replace(',', '.'))
    const pesoBrutoNumero = parseFloat(pesoBruto.replace(',', '.'))

    return {
      tipo,
      data: entidade?.data || new Date().toISOString(),
      valor: valorTotal,
      numeroNFeOrigem: numeroNFeOrigem.trim(),
      nFeReferencia: numeroNFeOrigem.trim(),
      destinatario: {
        nome: destinatario.toUpperCase().trim(),
        documento: documento.trim(),
        cnpj: documento.trim(),
        inscricaoEstadual: inscricaoEstadual.trim(),
        ie: inscricaoEstadual.trim(),
        cep: cep.trim(),
        endereco: {
          logradouro: endereco.toUpperCase().trim(),
          numero: numeroEndereco.trim(),
          complemento: complemento.toUpperCase().trim(),
          bairro: bairro.toUpperCase().trim(),
          codigoMunicipio: codigoMunicipio.trim(),
          municipio: cidade.toUpperCase().trim(),
          uf: uf.toUpperCase().trim(),
          cep: cep.trim(),
          telefone: telefone.trim()
        },
        numero: numeroEndereco.trim(),
        complemento: complemento.toUpperCase().trim(),
        bairro: bairro.toUpperCase().trim(),
        codigoMunicipio: codigoMunicipio.trim(),
        cidade: cidade.toUpperCase().trim(),
        uf: uf.toUpperCase().trim(),
        telefone: telefone.trim()
      },
      transporte: {
        tipoFrete,
        nome: transportadora.toUpperCase().trim(),
        cnpj: transportadoraCnpj.trim(),
        inscricaoEstadual: transportadoraIe.trim(),
        endereco: transportadoraEndereco.toUpperCase().trim(),
        cidade: transportadoraCidade.toUpperCase().trim(),
        uf: transportadoraUf.toUpperCase().trim()
      },
      volumens: {
        quantidadeVolumes: Number.isNaN(quantidadeVolumesNumero) ? 0 : quantidadeVolumesNumero,
        pesoLiquido: Number.isNaN(pesoLiquidoNumero) ? 0 : pesoLiquidoNumero,
        pesoBruto: Number.isNaN(pesoBrutoNumero) ? 0 : pesoBrutoNumero
      },
      itens: [...produtos]
    }
  }

  const handleSalvar = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (somenteLeitura) {
      return
    }

    const payloadCompleto = construirPayload()

    if (entidade && onAtualizar) {
      onAtualizar({
        ...payloadCompleto,
        id: entidade.id
      })
    } else {
      onAdicionar(payloadCompleto)
    }

    limparFormulario()
    setAberto(false)
  }

  const handleGerarEspelho = () => {
    if (onGerarEspelho) {
      onGerarEspelho(construirPayload())
    }
  }

  return (
    <Dialog fullScreen={fullScreen} open={aberto} onClose={handleFechar} maxWidth="lg" fullWidth>
      <DialogTitle>{tituloDialog}</DialogTitle>
      <DialogContent>
        <FormContainer id="formNovaNotaFiscal" onSubmit={handleSalvar}>
          {statusRetornoVisivel && (
            <SectionCard>
              <SectionTitle>Nota Fiscal</SectionTitle>
              <Row>
                <Col4>
                  <Typography component="span" fontWeight="bold">Status Retorno:</Typography>
                  <Typography
                    component="span"
                    sx={{ ml: 1, color: statusRetornoCor, fontWeight: 'bold' }}
                  >
                    {statusRetornoTexto}
                  </Typography>
                </Col4>
                <Col8>
                  <Typography component="span" fontWeight="bold">Mensagem Retorno:</Typography>
                  <Typography component="span" sx={{ ml: 1 }}>
                    {entidade?.mensagemRetorno || '-'}
                  </Typography>
                </Col8>
              </Row>
            </SectionCard>
          )}
          <SectionCard>
            <SectionTitle>Dados Principais</SectionTitle>
            <Row>
              <Col4>
                <Campo
                  label="Tipo"
                  value={tipoDescricaoAtual}
                  InputProps={{ readOnly: true }}
                  disabled
                  required
                />
              </Col4>
              <Col8>
                <Campo
                  label="Número NFe Origem"
                  type="number"
                  value={numeroNFeOrigem}
                  onChange={(event) => setNumeroNFeOrigem(event.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  {...camposSomenteLeituraProps}
                  required
                />
              </Col8>
            </Row>
          </SectionCard>

          <SectionCard>
            <SectionTitle>Destinatário</SectionTitle>
            <Row>
              <Col8>
                <Campo
                  label="Nome"
                  value={destinatario}
                  onChange={(event) => setDestinatario(event.target.value)}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  {...camposSomenteLeituraProps}
                  required
                />
              </Col8>
              <Col4>
                <Campo
                  label="CNPJ"
                  value={documento}
                  onChange={(event) => setDocumento(onlyDigits(event.target.value))}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  {...camposSomenteLeituraProps}
                  required
                />
              </Col4>
              <Col4>
                <Campo
                  label="Inscrição Estadual"
                  value={inscricaoEstadual}
                  onChange={(event) => setInscricaoEstadual(onlyDigits(event.target.value))}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  {...camposSomenteLeituraProps}
                />
              </Col4>
              <Col4>
                <Campo
                  label="CEP"
                  value={cep}
                  onChange={(event) => setCep(onlyDigits(event.target.value))}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  {...camposSomenteLeituraProps}
                />
              </Col4>
              <Col8>
                <Campo
                  label="Endereço"
                  value={endereco}
                  onChange={(event) => setEndereco(event.target.value)}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  {...camposSomenteLeituraProps}
                />
              </Col8>
              <Col4>
                <Campo
                  label="Número"
                  type="number"
                  value={numeroEndereco}
                  onChange={(event) => setNumeroEndereco(event.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  {...camposSomenteLeituraProps}
                />
              </Col4>
              <Col6>
                <Campo
                  label="Complemento"
                  value={complemento}
                  onChange={(event) => setComplemento(event.target.value)}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  {...camposSomenteLeituraProps}
                />
              </Col6>
              <Col6>
                <Campo
                  label="Bairro"
                  value={bairro}
                  onChange={(event) => setBairro(event.target.value)}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  {...camposSomenteLeituraProps}
                />
              </Col6>
              <Col4>
                <Campo
                  label="Município"
                  value={cidade}
                  onChange={(event) => setCidade(event.target.value)}
                  inputProps={{ style: { textTransform: 'uppercase' } }}
                  {...camposSomenteLeituraProps}
                />
              </Col4>
              <Col4>
                <Campo
                  label="Código Município"
                  type="number"
                  value={codigoMunicipio}
                  onChange={(event) => setCodigoMunicipio(event.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                  {...camposSomenteLeituraProps}
                />
              </Col4>
              <Col4>
                <Campo
                  label="UF"
                  value={uf}
                  onChange={(event) => setUf(event.target.value)}
                  inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                  {...camposSomenteLeituraProps}
                />
              </Col4>
              <Col4>
                <Campo
                  label="Telefone"
                  value={telefone}
                  onChange={(event) => setTelefone(onlyDigits(event.target.value))}
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  {...camposSomenteLeituraProps}
                />
              </Col4>
            </Row>
          </SectionCard>

          <SectionCard>
            <NotaFiscalProdutos
              produtos={produtos}
              onProdutosChange={setProdutos}
              somenteLeitura={somenteLeitura}
              produtosDisponiveis={produtosNaoSelecionados}
            />
          </SectionCard>

          <SectionCard>
            <SectionTitle>Transporte</SectionTitle>
            <Row>
              <Col6>
                <Campo
                  select
                  label="Tipo de Frete"
                  value={tipoFrete}
                  onChange={(event) => setTipoFrete(event.target.value)}
                  SelectProps={{ readOnly: somenteLeitura }}
                  disabled={somenteLeitura}
                >
                  <MenuItem value="Emitente">Emitente</MenuItem>
                  <MenuItem value="Destinatário">Destinatário</MenuItem>
                  <MenuItem value="Sem Frete">Sem Frete</MenuItem>
                </Campo>
              </Col6>
              {!semFreteSelecionado && (
                <>
                  <Col6>
                    <Campo
                      label="Nome"
                      value={transportadora}
                      onChange={(event) => setTransportadora(event.target.value)}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col6>
                  <Col4>
                    <Campo
                      label="CNPJ"
                      value={transportadoraCnpj}
                      onChange={(event) => setTransportadoraCnpj(onlyDigits(event.target.value))}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col4>
                  <Col4>
                    <Campo
                      label="Inscrição Estadual"
                      value={transportadoraIe}
                      onChange={(event) => setTransportadoraIe(onlyDigits(event.target.value))}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col4>
                  <Col8>
                    <Campo
                      label="Endereço"
                      value={transportadoraEndereco}
                      onChange={(event) => setTransportadoraEndereco(event.target.value)}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col8>
                  <Col6>
                    <Campo
                      label="Cidade"
                      value={transportadoraCidade}
                      onChange={(event) => setTransportadoraCidade(event.target.value)}
                      inputProps={{ style: { textTransform: 'uppercase' } }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col6>
                  <Col3>
                    <Campo
                      label="UF"
                      value={transportadoraUf}
                      onChange={(event) => setTransportadoraUf(event.target.value)}
                      inputProps={{ maxLength: 2, style: { textTransform: 'uppercase' } }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col3>
                </>
              )}
            </Row>
            {!semFreteSelecionado && (
              <>
                <SectionTitle style={{ marginTop: 12 }}>Volumes</SectionTitle>
                <Row>
                  <Col4>
                    <Campo
                      label="Quantidade Volumes"
                      type="number"
                      value={quantidadeVolumes}
                      onChange={(event) => setQuantidadeVolumes(event.target.value)}
                      inputProps={{ min: 0, step: 1 }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col4>
                  <Col4>
                    <Campo
                      label="Peso líquido"
                      type="number"
                      value={pesoLiquido}
                      onChange={(event) => setPesoLiquido(event.target.value)}
                      inputProps={{ min: 0, step: 'any' }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col4>
                  <Col4>
                    <Campo
                      label="Peso bruto"
                      type="number"
                      value={pesoBruto}
                      onChange={(event) => setPesoBruto(event.target.value)}
                      inputProps={{ min: 0, step: 'any' }}
                      {...camposSomenteLeituraProps}
                    />
                  </Col4>
                </Row>
              </>
            )}
          </SectionCard>
        </FormContainer>
      </DialogContent>
      <DialogActions>
        {somenteLeitura ? (
          <Button onClick={handleFechar}>Fechar</Button>
        ) : (
          <>
            <Button onClick={handleFechar}>Cancelar</Button>
            {!entidade && onGerarEspelho && (
              <Button
                onClick={handleGerarEspelho}
                disabled={produtos.length === 0 || !numeroNFeOrigem.trim() || !destinatario.trim() || !documento.trim()}
                color="info"
              >
                Gerar Espelho de Nota
              </Button>
            )}
            <ButtonSalvar
              type="submit"
              form="formNovaNotaFiscal"
              disabled={produtos.length === 0 || !numeroNFeOrigem.trim() || !destinatario.trim() || !documento.trim()}
            >
              Salvar
            </ButtonSalvar>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default NotaFiscalDialog
